'use client';

import { useMemo, useContext } from 'react';

import { itemByLocales, RealEstateDict } from '@monoimin/intl';

import { ByLocales, type EnOrRuLocale } from '@monoimin/sb-types';

import { Disclosure } from '@monoimin/molecules/Disclosure';

import { useParamsUpdater } from '@monoimin/hooks';

import {
  createDevelopersForFilter,
  createOptionsForFilter,
  findParamsSlugMatch,
  createObjectTypesForFilter,
  separateFilterConfigs,
  extendFilterConfigs,
} from '../../utils/filters';
import { ESTATE_FILTER_KEYS, FILTER_VARIANTS } from '../../constants';

import { FiltersContext } from './FiltersProvider';

import { RangeFilter } from './RangeFilter';

import { SelectFilter } from './SelectFilter';

import { CountryFilter } from './CountryFilter';

import { CityFilter } from './CityFilter';

import { FilterInput } from './InputFilter';

import { OptionFilters } from './OptionFilters';

import type {
  FiltersData,
  FilterConfigs,
  SelectItem,
  ExtendedFilterConfigs,
  EstateFillterKeys,
} from '../../types';

type RenderFilterConfigsProps = FiltersData & {
  className?: string;
  configs: FilterConfigs | ExtendedFilterConfigs;
  realEstateDict: RealEstateDict;
  newPathname?: string;
  locale: EnOrRuLocale;
};

export const RenderFilterConfigs = ({
  className,
  developers = [],
  costForGreeceResponse,
  objectTypes,
  configs,
  countries,
  realEstateDict,
  cities,
  newPathname,
  locale,
}: RenderFilterConfigsProps) => {
  const [filtersState] = useContext(FiltersContext);

  const { searchParams, onChange: onParamsChange } = useParamsUpdater();

  // Стоит брать из стейта
  const countryParams = searchParams?.getAll(ESTATE_FILTER_KEYS.COUNTRY);

  const objectTypesForFilter = useMemo(() => {
    if (objectTypes?.data) {
      return createObjectTypesForFilter(Object.keys(objectTypes.data), locale);
    }

    return [];
  }, [objectTypes?.data, locale]);

  const developersForFilter = useMemo(() => {
    if (Array.isArray(countryParams) && countryParams?.length) {
      const filtered = developers.filter((item) => {
        return findParamsSlugMatch(countryParams, item.countries);
      });

      return createDevelopersForFilter(filtered);
    }

    return createDevelopersForFilter(developers);
  }, [developers, countryParams]);

  const costForGreece = useMemo(() => {
    if (costForGreeceResponse?.data) {
      return createOptionsForFilter(Object.keys(costForGreeceResponse.data));
    }

    return [];
  }, [costForGreeceResponse]);

  const { visibleConfigs, hiddenConfigs } = useMemo(() => {
    const extendedFilterConfigs = extendFilterConfigs(configs);

    return separateFilterConfigs(extendedFilterConfigs);
  }, [configs]);

  const defineItemsForFilter = (
    filterKey: EstateFillterKeys,
    filterItems: ByLocales<EnOrRuLocale, Array<SelectItem>>
  ) => {
    if (filterKey === ESTATE_FILTER_KEYS.DEVELOPER) {
      return itemByLocales(developersForFilter);
    }

    if (filterKey === ESTATE_FILTER_KEYS.COST_FOR_GR) {
      return itemByLocales(costForGreece);
    }

    if (filterKey === ESTATE_FILTER_KEYS.OBJECT_TYPE) {
      return itemByLocales(objectTypesForFilter);
    }

    return filterItems;
  };

  const disclosureProps = Disclosure.createProps({
    titleProps: {
      close: realEstateDict.showMoreOptions,
      open: realEstateDict.showLessOptions,
      children: null,
    },
    classNames: {
      disclosure: 'col-span-full pt-3',
      panel: className,
    },
  });

  const renderConfigs = (configs: ExtendedFilterConfigs) => {
    return configs.map((item) => {
      const { filterVariant, options, filterKey, className } = item;

      if (filterVariant === FILTER_VARIANTS.RANGE) {
        return (
          <OptionFilters
            options={options}
            key={filterKey.from}
            locale={locale}
            className={className}
          >
            <RangeFilter
              {...item}
              realEstateDict={realEstateDict}
              fromField={filterKey.from}
              toField={filterKey.to}
              placeholder={
                realEstateDict[item.placeholderDictionaryKey] as string
              }
              fromPlaceholder={
                realEstateDict[item.fromPlaceholderDictionaryKey] as string
              }
              toPlaceholder={
                realEstateDict[item.toPlaceholderDictionaryKey] as string
              }
              newPathname={newPathname}
              valueFormater={item.valueFormatter}
            />
          </OptionFilters>
        );
      }

      if (filterVariant === FILTER_VARIANTS.SELECT) {
        if (filterKey === ESTATE_FILTER_KEYS.COUNTRY) {
          return (
            <OptionFilters
              options={options}
              key={filterKey}
              locale={locale}
              className={className}
            >
              <CountryFilter
                variant='desktop'
                countries={countries}
                filtersState={filtersState}
                onChange={(newState) => {
                  onParamsChange(newState, newPathname);
                }}
                realEstateDict={realEstateDict}
              />
            </OptionFilters>
          );
        }

        if (filterKey === ESTATE_FILTER_KEYS.CITY) {
          return (
            <OptionFilters
              options={options}
              key={filterKey}
              locale={locale}
              className={className}
            >
              <CityFilter
                variant='desktop'
                cities={cities}
                countries={countries}
                filtersState={filtersState}
                onChange={(newState) => {
                  onParamsChange(newState, newPathname);
                }}
                realEstateDict={realEstateDict}
              />
            </OptionFilters>
          );
        }

        const filterItems = defineItemsForFilter(filterKey, item.filterItems);

        return (
          <OptionFilters
            options={options}
            key={filterKey}
            locale={locale}
            className={className}
          >
            <SelectFilter
              {...item}
              filterItems={filterItems}
              newPathname={newPathname}
              realEstateDict={realEstateDict}
            />
          </OptionFilters>
        );
      }

      if (filterVariant === FILTER_VARIANTS.INPUT) {
        return (
          <OptionFilters
            options={options}
            key={filterKey}
            locale={locale}
            className={className}
          >
            <FilterInput
              {...item}
              filteKey={filterKey}
              realEstateDict={realEstateDict}
            />
          </OptionFilters>
        );
      }

      return null;
    });
  };

  return (
    <div className={className}>
      {renderConfigs(visibleConfigs)}
      {hiddenConfigs?.length > 0 && (
        <Disclosure {...disclosureProps}>
          {renderConfigs(hiddenConfigs)}
        </Disclosure>
      )}
    </div>
  );
};
